import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  CONDITION_OPERATOR_OPTIONS,
  RECORD_TYPE_OPTIONS,
  RETENTION_ROUTE,
  applyRetentionSelectionChange,
  buildSampleRetentionRules,
  createEmptyCondition,
  createEmptyRetentionForm,
  formatConditionsPreview,
  formatRetentionDateTime,
  getAvailableConditionFields,
  getConditionFieldLabel,
  getConditionValueLabel,
  getConditionValueOptions,
  getFinalActionLabel,
  getFinalActionOptions,
  getFinalActionScope,
  getRecordTypeCoverage,
  getRecordTypeReference,
  getRetentionPeriodCalculation,
  getRetentionPeriodLabel,
  getRetentionPeriodOptions,
  getRetentionRecordTypeLabel,
  getStartsWhenDateTimeBasis,
  getStartsWhenLabel,
  getStartsWhenMeaning,
  getStartsWhenOptions,
  normalizeRetentionForm,
  operatorIsMulti,
} from '../../../data/retentionConfig';
import {
  canAddRetentionRule,
  canEditRetentionRule,
  canSaveRetentionRule,
  canViewRetentionConfiguration,
  getEditRuleSource,
  getPublishedRule,
  saveRetentionRule,
} from '../../../data/retentionRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyRetentionForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeRetentionForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2 border border-lightgray rounded-xl px-3 py-3 max-h-56 overflow-y-auto">
      {disabled || !options.length ? (
        <AdminSupportingText className="text-mediumgray">
          {disabled ? 'Complete the previous selections first.' : 'No options available.'}
        </AdminSupportingText>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-sm text-offblack">
            <input
              type="checkbox"
              className="accent-primaryblue mt-0.5 shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: getLabel(value) || `${value} Unavailable` }];
};

const RetentionRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewRetentionConfiguration(auth);
  const canAdd = canAddRetentionRule(auth);
  const canEdit = canEditRetentionRule(auth);
  const canSave = canSaveRetentionRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleRetentionRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const recordTypeOptions = useMemo(
    () => withCurrentOption(RECORD_TYPE_OPTIONS, formData.recordType, (value) => getRetentionRecordTypeLabel(value)),
    [formData.recordType],
  );
  const startsWhenOptions = useMemo(
    () => withCurrentOption(getStartsWhenOptions(formData.recordType), formData.startsWhen, getStartsWhenLabel),
    [formData.recordType, formData.startsWhen],
  );
  const periodOptions = useMemo(
    () => withCurrentOption(
      getRetentionPeriodOptions(formData.recordType),
      formData.retentionPeriod,
      getRetentionPeriodLabel,
    ),
    [formData.recordType, formData.retentionPeriod],
  );
  const finalActionOptions = useMemo(
    () => withCurrentOption(
      getFinalActionOptions(formData.recordType),
      formData.finalAction,
      (value) => getFinalActionLabel(value, formData.recordType),
    ),
    [formData.recordType, formData.finalAction],
  );
  const conditionFields = useMemo(
    () => getAvailableConditionFields(formData.recordType, formData.startsWhen),
    [formData.recordType, formData.startsWhen],
  );
  const canAddCondition = conditionFields.length > 0;
  const conditionsPreview = formatConditionsPreview(formData.conditions);

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const handleChange = (field, value) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        duplicate: undefined,
        conflict: undefined,
        overlap: undefined,
        differentStart: undefined,
        concurrent: undefined,
        form: undefined,
        permission: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
    setFormData((prev) => applyRetentionSelectionChange(prev, field, value));
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      conditions[index] = { ...conditions[index], ...patch };
      if (patch.field) conditions[index].values = [];
      if (patch.operator && !operatorIsMulti(patch.operator)) {
        conditions[index].values = (conditions[index].values || []).slice(0, 1);
      }
      return { ...prev, conditions };
    });
  };

  const handleSave = () => {
    if (isCreate && !canAdd) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    if (isEdit && !canEdit) {
      setErrors({ permission: 'You do not have permission to save this Rule.' });
      return;
    }
    const result = saveRetentionRule(allRules, formData, publishedBaseline || {}, {
      originalLastUpdated: resolvedSource.lastUpdated,
      canSave,
    });
    if (!result.success) {
      setErrors({
        ...result.errors,
        form: result.summary?.length ? result.summary.join(' ') : 'Fix the highlighted fields.',
      });
      return;
    }
    navigate(RETENTION_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required && <span className="text-alertred">*</span>}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`ret-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible choices available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] && <AdminFormFieldError>{errors[name]}</AdminFormFieldError>}
      </div>
    );
  };

  const formTitle = isCreate ? 'Add Retention Rule' : 'Edit Retention Rule';

  const hasValidationErrors = Boolean(
    errors.permission
    || errors.checksUnavailable
    || errors.duplicate
    || errors.conflict
    || errors.overlap
    || errors.differentStart
    || errors.concurrent
    || errors.form
    || errors.conditions,
  );

  if (!canView && isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Retention Rule Detail</AdminPageTitle>
        <AdminFormFieldError>You do not have permission to view this Rule.</AdminFormFieldError>
      </AdminLayout>
    );
  }

  if (isView) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Retention Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue label="Record Type" value={getRetentionRecordTypeLabel(formData.recordType)} />
              <TextValue label="Record Type reference" value={getRecordTypeReference(formData.recordType) || '—'} mono />
              <div className="sm:col-span-2">
                <TextValue label="Record Type coverage" value={getRecordTypeCoverage(formData.recordType) || '—'} />
              </div>
              <TextValue label="Starts When" value={getStartsWhenLabel(formData.startsWhen)} />
              <TextValue label="Date/time basis" value={getStartsWhenDateTimeBasis(formData.startsWhen) || '—'} />
              <div className="sm:col-span-2">
                <TextValue label="Starts When meaning" value={getStartsWhenMeaning(formData.startsWhen) || '—'} />
              </div>
              <TextValue label="Retention Period" value={getRetentionPeriodLabel(formData.retentionPeriod)} />
              <div className="sm:col-span-2">
                <TextValue label="Period calculation" value={getRetentionPeriodCalculation(formData.retentionPeriod) || '—'} />
              </div>
              <TextValue label="Final Action" value={getFinalActionLabel(formData.finalAction, formData.recordType)} />
              <div className="sm:col-span-2">
                <TextValue label="Final Action scope" value={getFinalActionScope(formData.finalAction) || '—'} />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : conditionsPreview.text}
                />
              </div>
              <TextValue label="Date Added" value={formatRetentionDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatRetentionDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={formTitle}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}

        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="ret-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName && <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError>}
        </div>

        {renderSelect('recordType', 'Record Type', recordTypeOptions, 'Select record type')}
        {formData.recordType ? (
          <>
            <AdminConfigLockedField
              label="Record Type coverage"
              value={getRecordTypeCoverage(formData.recordType) || '—'}
              isView={false}
            />
            <AdminConfigLockedField
              label="Record Type reference"
              value={getRecordTypeReference(formData.recordType) || '—'}
              mono
              isView={false}
            />
          </>
        ) : null}
        {renderSelect(
          'startsWhen',
          'Starts When',
          startsWhenOptions,
          formData.recordType ? 'Select when retention starts' : 'Select record type first',
          !formData.recordType,
        )}
        {formData.startsWhen ? (
          <>
            <AdminConfigLockedField
              label="Starts When meaning"
              value={getStartsWhenMeaning(formData.startsWhen) || '—'}
              isView={false}
            />
            <AdminConfigLockedField
              label="Date/time basis"
              value={getStartsWhenDateTimeBasis(formData.startsWhen) || '—'}
              isView={false}
            />
          </>
        ) : null}
        {renderSelect(
          'retentionPeriod',
          'Retention Period',
          periodOptions,
          formData.recordType ? 'Select retention period' : 'Select record type first',
          !formData.recordType,
        )}
        {formData.retentionPeriod ? (
          <AdminConfigLockedField
            label="Period calculation"
            value={getRetentionPeriodCalculation(formData.retentionPeriod) || '—'}
            isView={false}
          />
        ) : null}
        {renderSelect(
          'finalAction',
          'Final Action',
          finalActionOptions,
          formData.recordType ? 'Select final action' : 'Select record type first',
          !formData.recordType,
        )}
        {formData.finalAction ? (
          <AdminConfigLockedField
            label="Final Action scope"
            value={getFinalActionScope(formData.finalAction) || '—'}
            isView={false}
          />
        ) : null}

        <div className="space-y-3">
          <AdminDetailFieldLabel>Optional Conditions</AdminDetailFieldLabel>
          {(formData.conditions || []).length ? (
            <div className="space-y-3">
              {(formData.conditions || []).map((condition, index) => {
                const valueOptions = withCurrentOption(
                  getConditionValueOptions(condition.field),
                  (condition.values || [])[0],
                  (value) => getConditionValueLabel(condition.field, value),
                );
                const multi = operatorIsMulti(condition.operator);
                return (
                  <div key={condition.id || index} className="rounded-lg border border-lightgray p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <AdminSupportingText className="mb-0 font-semibold !text-offblack">Condition</AdminSupportingText>
                      <button
                        type="button"
                        onClick={() => handleChange('conditions', (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index))}
                        className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                        aria-label="Remove Condition"
                      >
                        <i className="fi fi-rs-trash text-sm" aria-hidden />
                      </button>
                    </div>
                    {errors[`condition_${index}`] ? <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError> : null}
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.field || ''}
                          onChange={(e) => updateCondition(index, { field: e.target.value })}
                          className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                        >
                          <option value="">Select field</option>
                          {withCurrentOption(conditionFields, condition.field, getConditionFieldLabel).map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    <div className="space-y-1 w-full min-w-0">
                      <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                      <div className="relative w-full min-w-0">
                        <select
                          value={condition.operator || ''}
                          onChange={(e) => updateCondition(index, { operator: e.target.value })}
                          disabled={!condition.field}
                          className={adminFormDetailSelectClasses(!condition.field)}
                        >
                          <option value="">Select operator</option>
                          {CONDITION_OPERATOR_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <AdminFormDropdownChevron />
                      </div>
                    </div>
                    {multi ? (
                      <MultiChecklist
                        label="Value"
                        options={getConditionValueOptions(condition.field)}
                        values={condition.values}
                        onChange={(value) => updateCondition(index, { values: value })}
                        disabled={!condition.field}
                        required
                      />
                    ) : (
                      <div className="space-y-1 w-full min-w-0">
                        <AdminDetailFieldLabel>Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                        <div className="relative w-full min-w-0">
                          <select
                            value={(condition.values || [])[0] || ''}
                            onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                            disabled={!condition.field || !valueOptions.length}
                            className={adminFormDetailSelectClasses(!condition.field || !valueOptions.length)}
                          >
                            <option value="">Select value</option>
                            {valueOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                          <AdminFormDropdownChevron />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <AdminDetailFieldValue className="py-2">
              {isCreate ? 'No Conditions added' : 'No Conditions'}
            </AdminDetailFieldValue>
          )}
          {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          {canAddCondition ? (
            <button
              type="button"
              onClick={() => handleChange('conditions', [...(formData.conditions || []), createEmptyCondition()])}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
            >
              <i className="fi fi-rs-plus text-xs" aria-hidden />
              Add Condition
            </button>
          ) : (
            <AdminSupportingText className="text-mediumgray">
              {formData.recordType && formData.startsWhen
                ? 'No compatible Conditions available'
                : 'Select Record Type and Starts When first.'}
            </AdminSupportingText>
          )}
        </div>

        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.differentStart ? <AdminFormFieldError>{errors.differentStart}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
          </div>
        ) : null}

        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default RetentionRuleDetail;
